import type { TrialState } from '../types';

import type {
  ConsoleAccess,
  PlgAccessInput,
  ReportingAccessInput,
} from './types';

const AUTOMATION_TRIAL_STARTED: ReadonlySet<TrialState> = new Set([
  'active',
  'grace-period',
  'post-trial',
]);

/** Org participates in reporting monetization (eligible and/or paid). */
export const doesReportingMonetizationApply = (
  reporting: ReportingAccessInput
): boolean => reporting.isEligible || reporting.state === 'paid';

/** PLG / Cast AI free trial has been started at least once. */
export const hasStartedAutomationTrial = (plg: PlgAccessInput): boolean =>
  AUTOMATION_TRIAL_STARTED.has(plg.state);

/**
 * Pure derivation of console capabilities from both trial domains.
 * Add new flags here — do not nest trial checks at call sites.
 */
export const deriveConsoleAccess = (
  reporting: ReportingAccessInput,
  plg: PlgAccessInput
): ConsoleAccess => {
  const reportingApplies = doesReportingMonetizationApply(reporting);
  const automationStarted = hasStartedAutomationTrial(plg);

  // Unlock only when reporting is paid AND PLG automation trial has started.
  // Eligible (unpaid) reporting stays limited regardless of PLG state.
  const limitAutomationConsole =
    reportingApplies && !(reporting.state === 'paid' && automationStarted);
  const showAutomationUpsell = reporting.state === 'paid' && !automationStarted;

  return {
    limitAutomationConsole,
    showReportingTrialBadge: reporting.isEligible && reporting.state !== 'paid',
    showAutomationUpsell,
    // Paid+pre-trial stays limited but Overview must stay reachable for upsell.
    showAutomationOverviewNav: !limitAutomationConsole || showAutomationUpsell,
  };
};
